const CAMADAS_ORDEM = ["QUINZENAL", "MENSAL", "TRIMESTRAL", "SEMESTRAL"];
const CONTRATOS_CAMADAS = {
  QUINZENAL: ["QUINZENAL", "MENSAL", "TRIMESTRAL", "SEMESTRAL"],
  MENSAL: ["MENSAL", "TRIMESTRAL", "SEMESTRAL"],
  TRIMESTRAL: ["TRIMESTRAL", "SEMESTRAL"],
  SEMESTRAL: ["SEMESTRAL"],
};

function normalizarCamada(value, fallback = "MENSAL") {
  const v = String(value || "").toUpperCase().trim();
  return CAMADAS_ORDEM.includes(v) ? v : fallback;
}

function normalizarContratoTipo(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";
  if (raw.includes("AVULSO")) return "AVULSO";
  if (raw.startsWith("QUINZ")) return "QUINZENAL";
  if (raw.startsWith("MENS")) return "MENSAL";
  if (raw.startsWith("TRIM")) return "TRIMESTRAL";
  if (raw.startsWith("SEMES")) return "SEMESTRAL";
  if (raw === "CONTRATO") return "MENSAL";
  return raw;
}

function camadasPermitidas(contratoTipo) {
  const key = normalizarContratoTipo(contratoTipo);
  if (!key) return CAMADAS_ORDEM;
  if (key === "AVULSO") return [];
  return CONTRATOS_CAMADAS[key] || CAMADAS_ORDEM;
}

function getContratoTipo(preventiva) {
  return (
    preventiva?.contratoTipo ||
    preventiva?.setorContratoTipo ||
    preventiva?.tipoContrato ||
    ""
  );
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "number") return new Date(value);
  return null;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfMonth(date) {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfQuarter(date) {
  const d = new Date(date);
  const mesInicioTrimestre = Math.floor(d.getMonth() / 3) * 3;
  d.setMonth(mesInicioTrimestre, 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfSemester(date) {
  const d = new Date(date);
  const mesInicioSemestre = d.getMonth() < 6 ? 0 : 6;
  d.setMonth(mesInicioSemestre, 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  d.setHours(0, 0, 0, 0);
  return d;
}

function withDay(date, day) {
  const d = new Date(date);
  d.setDate(day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function proximoInicioQuinzena(date) {
  const ref = startOfDay(date || new Date());
  const dia = ref.getDate();
  const baseMes = startOfMonth(ref);
  if (dia <= 14) return withDay(baseMes, 15);
  return addMonths(baseMes, 1);
}

function proximaDataInicialContrato(contratoTipo, referencia = new Date()) {
  const ref = startOfDay(referencia || new Date());
  const contratoKey = normalizarContratoTipo(contratoTipo);

  if (contratoKey === "QUINZENAL") return proximoInicioQuinzena(ref);
  if (contratoKey === "TRIMESTRAL") return startOfQuarter(ref);
  if (contratoKey === "SEMESTRAL") return startOfSemester(ref);
  if (contratoKey === "MENSAL") return startOfMonth(ref);

  return startOfMonth(ref);
}

function hasCamadas(preventiva, camadas = CAMADAS_ORDEM) {
  return camadas.some((c) => {
    const campo = {
      QUINZENAL: "lastQuinzenal",
      MENSAL: "lastMensal",
      TRIMESTRAL: "lastTrimestral",
      SEMESTRAL: "lastSemestral",
    }[c];
    return !!toDate(preventiva?.[campo]);
  });
}

function getLastDate(preventiva, camada, camadas = CAMADAS_ORDEM) {
  if (!preventiva) return null;
  const campo = {
    QUINZENAL: "lastQuinzenal",
    MENSAL: "lastMensal",
    TRIMESTRAL: "lastTrimestral",
    SEMESTRAL: "lastSemestral",
  }[camada];

  const valor = campo ? preventiva?.[campo] : null;
  const date = toDate(valor);
  if (date) return date;

  // Quando um campo de camada ainda não existe, usamos ultimaExecucaoCiclo (competência)
  // como base, para não considerar a camada "vencida para sempre" por falta do campo.
  const fallbackCiclo = toDate(preventiva?.ultimaExecucaoCiclo);
  if (fallbackCiclo) return fallbackCiclo;

  // Se ainda não existir, cai no valor real (legado).
  const fallback = toDate(preventiva?.ultimaExecucao);
  if (fallback) return fallback;

  return null;
}

function proximaDataCamada(preventiva, camada, referencia = new Date(), contratoTipo) {
  const camadas = camadasPermitidas(contratoTipo);
  if (!camadas.includes(camada)) return null;

  const ref = startOfDay(referencia);
  const last = getLastDate(preventiva, camada, camadas);
  if (!last) return proximaDataInicialContrato(contratoTipo, ref);

  if (camada === "QUINZENAL") {
    // Contrato quinzenal: duas execuções por mês
    // - Se a última foi na 1ª quinzena (dia 1..14) -> próxima em 15 do mesmo mês
    // - Se a última foi na 2ª quinzena (dia 15..31) -> próxima no início do mês seguinte
    const lastDia = startOfDay(last);
    const baseMes = startOfMonth(lastDia);
    const dia = lastDia.getDate();
    if (dia <= 14) return withDay(baseMes, 15);
    return addMonths(baseMes, 1);
  }

  const base = startOfMonth(last);
  let meses = 1;
  if (camada === "TRIMESTRAL") meses = 3;
  if (camada === "SEMESTRAL") meses = 6;

  const proxBase = addMonths(base, meses);
  // Para contrato quinzenal, camadas MENSAL/TRIMESTRAL/SEMESTRAL vencem no início do mês (dia 1).
  // A 2ª quinzena (>=16) continua podendo executar camadas altas se estiverem vencidas (calcularCamadaExecutar).
  return proxBase;
}

function isCamadaVencida(preventiva, camada, referencia = new Date(), contratoTipo) {
  const prox = proximaDataCamada(preventiva, camada, referencia, contratoTipo);
  if (!prox) return false;
  const ref = startOfDay(referencia);
  return prox.getTime() <= ref.getTime();
}

export function calcularCamadaExecutar(preventiva, referencia = new Date()) {
  if (!preventiva) return null;

  const forcar = normalizarCamada(preventiva?.camadaForcada, "");
  if (forcar) return forcar;

  const contratoTipo = getContratoTipo(preventiva);
  const camadas = camadasPermitidas(contratoTipo);
  if (camadas.length === 0) return null;

  const primeiraExecucao = !hasCamadas(preventiva, camadas);
  // Regra: a primeira execução do contrato é sempre SEMESTRAL.
  if (primeiraExecucao) {
    if (camadas.includes("SEMESTRAL")) return "SEMESTRAL";
    return camadas[camadas.length - 1] || null;
  }


  const ref = startOfDay(referencia);
  const contratoKey = normalizarContratoTipo(contratoTipo);
  const ordemDesc = [...CAMADAS_ORDEM].reverse();

  if (contratoKey === "QUINZENAL") {
    // Contrato quinzenal:
    // - 1ª quinzena (dia 1..14): sempre executa QUINZENAL
    // - 2ª quinzena (>=15): executa camadas altas vencidas (mensal/trimestral/semestral); senão, QUINZENAL
    if (ref.getDate() < 15) return "QUINZENAL";

    const camadasAltas = ordemDesc.filter((c) => c !== "QUINZENAL" && camadas.includes(c));
    for (let i = 0; i < camadasAltas.length; i += 1) {
      const camada = camadasAltas[i];
      if (isCamadaVencida(preventiva, camada, ref, contratoTipo)) {
        return camada;
      }
    }
    return "QUINZENAL";
  }
  for (let i = 0; i < ordemDesc.length; i += 1) {
    const camada = ordemDesc[i];
    if (!camadas.includes(camada)) continue;
    if (isCamadaVencida(preventiva, camada, ref, contratoTipo)) {
      return camada;
    }
  }

  return null;
}

export function filtrarItensPorCamada(itens, camada) {
  if (!Array.isArray(itens) || itens.length === 0) return [];
  if (!camada) return [];

  const idxMax = CAMADAS_ORDEM.indexOf(normalizarCamada(camada));
  return itens.filter((item) => {
    const itemCamada = normalizarCamada(item?.periodicidade || "MENSAL");
    const idxItem = CAMADAS_ORDEM.indexOf(itemCamada);
    return idxItem <= idxMax;
  });
}

export function montarAtualizacaoCamadas(camada, data, contratoTipo) {
  if (!camada) return {};
  const idxMax = CAMADAS_ORDEM.indexOf(normalizarCamada(camada));
  const camadas = camadasPermitidas(contratoTipo);
  const payload = {};

  CAMADAS_ORDEM.forEach((c, idx) => {
    if (idx <= idxMax && camadas.includes(c)) {
      const campo = {
        QUINZENAL: "lastQuinzenal",
        MENSAL: "lastMensal",
        TRIMESTRAL: "lastTrimestral",
        SEMESTRAL: "lastSemestral",
      }[c];
      payload[campo] = data;
    }
  });

  return payload;
}

export function calcularProximaDataCamadas(preventiva, referencia = new Date()) {
  const ref = startOfDay(referencia || new Date());
  const contratoTipo = getContratoTipo(preventiva);
  const camadas = camadasPermitidas(contratoTipo);
  if (camadas.length === 0) return null;

  const datas = camadas
    .map((camada) => proximaDataCamada(preventiva, camada, ref, contratoTipo))
    .filter(Boolean);

  if (datas.length === 0) return null;

  let menor = datas[0];
  datas.forEach((d) => {
    if (d && d.getTime() < menor.getTime()) {
      menor = d;
    }
  });

  return menor;
}

export function formatarCamadaLabel(camada) {
  const c = normalizarCamada(camada, "");
  if (c === "QUINZENAL") return "Quinzenal";
  if (c === "MENSAL") return "Mensal";
  if (c === "TRIMESTRAL") return "Trimestral";
  if (c === "SEMESTRAL") return "Semestral";
  return "";
}

export function formatarCamadaDetalhe(camada) {
  const c = normalizarCamada(camada, "");
  if (c === "QUINZENAL") return "Quinzenal";
  if (c === "MENSAL") return "Mensal (inclui Quinzenal)";
  if (c === "TRIMESTRAL") return "Trimestral (inclui Quinzenal + Mensal + Trimestral)";
  if (c === "SEMESTRAL") return "Semestral (Completa)";
  return "";
}

/**
 * Calcula a primeira data de preventiva para um contrato após instalação.
 * Para Mensal: 1º do próximo mês
 * Para Quinzenal: 1º ou 15º do próximo período
 * Para Trimestral: 1º do próximo trimestre
 * Para Semestral: 1º do próximo semestre
 */
export function proximaDataPosInstalacaoComContrato(contratoTipo, dataInstalacao = new Date()) {
  const tipo = normalizarContratoTipo(contratoTipo);
  if (!tipo || tipo === "AVULSO") return null;

  const data = new Date(dataInstalacao);
  data.setHours(0, 0, 0, 0);

  if (tipo === "QUINZENAL") {
    // Próxima quinzena: se instalou nos primeiros 14 dias, próxima é dia 15; senão é 1º do próximo mês
    if (data.getDate() <= 14) {
      data.setDate(15);
    } else {
      data.setMonth(data.getMonth() + 1);
      data.setDate(1);
    }
    return data;
  }

  if (tipo === "MENSAL") {
    // Próximo mês, dia 1º
    data.setMonth(data.getMonth() + 1);
    data.setDate(1);
    return data;
  }

  if (tipo === "TRIMESTRAL") {
    // Próximo trimestre, dia 1º
    const mesAtual = data.getMonth();
    const trimestre = Math.floor(mesAtual / 3);
    const proximoTrimestre = trimestre + 1;
    const novoMes = proximoTrimestre * 3;

    if (novoMes >= 12) {
      data.setFullYear(data.getFullYear() + 1);
      data.setMonth(0);
    } else {
      data.setMonth(novoMes);
    }
    data.setDate(1);
    return data;
  }

  if (tipo === "SEMESTRAL") {
    // Próximo semestre, dia 1º
    const mesAtual = data.getMonth();
    const semestre = Math.floor(mesAtual / 6);
    const proximoSemestre = semestre + 1;
    const novoMes = proximoSemestre * 6;

    if (novoMes >= 12) {
      data.setFullYear(data.getFullYear() + 1);
      data.setMonth(0);
    } else {
      data.setMonth(novoMes);
    }
    data.setDate(1);
    return data;
  }

  return null;
}
